import { TableWithStoredSettingsAndLayoutSettings } from '@Components/Tables/TableWithStoredSettingsAndLayoutSettings';
import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { registerContainerRentNotification, useBookContainerRent } from '@Hooks/Orders';
import { emptyPage } from '@Hooks/useListApi';
import { useRemoteCacheInvalidation } from '@Hooks/useRemoteCacheInvalidation';
import { useAuftragRepository } from '@Hooks/useRepository';
import { type IHasTableModes } from '@Hooks/useTableModes';
import { type ApiUnbilledContainerRentGroup, EnumFrontendLayoutSetting } from '@Model';
import { ContainerRentRepository } from '@Repositories/Auftragsbearbeitung';
import {
    Badge,
    Button,
    DateInputBase,
    defaultIconSize,
    type ExpandedState,
    FlexColumn,
    FloatingToolbar,
    type ListInfo,
    type ListRequest,
    type ToolbarOptions,
    unwrapOr,
    useTableRefetch,
    useToasts,
} from '@synqo/frontend-component-library';
import { IconCircleCheck } from '@tabler/icons-react';
import { useCallback, useState } from 'react';
import {
    ContainerStockLines,
    dateOf,
    dayOf,
    isInTheFuture,
    monthsBefore,
    toggledExpansion,
    unbilledContainerRentColumns,
    unbilledContainerRentLineColumns,
    useUnbilledContainerRentSelection,
} from '../Editing';
import { type ContainerRentTableModes } from './containerRentTableModes';
import { useContainerStockTableModeToolbar } from './useContainerStockTableModeToolbar';

/** How far back from the cutoff the list looks at first. */
const LOOKBACK_MONTHS = 6;

/** The standing times with unbilled rent, grouped by customer, site and container type. */
// @ai-generated 2026-10-21T10:12:41+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function FrameListUnbilledContainerRent(tableModeProps: IHasTableModes<ContainerRentTableModes>) {
    const hasPermission = useHasPermission();
    const { pushToast } = useToasts();
    const containerRent = useAuftragRepository(ContainerRentRepository);
    const { bookContainerRent, isBookingPending } = useBookContainerRent();

    const { selected, groupSelector, lineSelectorOf, clear, track } = useUnbilledContainerRentSelection();
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [billedFrom, setBilledFrom] = useState(() => monthsBefore(new Date(), LOOKBACK_MONTHS));
    const [billedUntil, setBilledUntil] = useState(() => dayOf(new Date()));
    const { refetchRef, refetchTable } = useTableRefetch();
    useRemoteCacheInvalidation('container_rent', refetchTable);

    const loadGroups = useCallback(
        async (request: ListRequest): Promise<ListInfo<ApiUnbilledContainerRentGroup>> => {
            const loaded = unwrapOr(
                await containerRent.getUnbilled(request, billedFrom, billedUntil),
                emptyPage<ApiUnbilledContainerRentGroup>(),
            );
            track(loaded.items);

            return loaded;
        },
        [containerRent, billedFrom, billedUntil, track],
    );

    const startBooking = useCallback(() => {
        clear();
        const startedId = pushToast({
            variant: 'info',
            title: 'Mietabrechnung',
            message: 'Die Abrechnung der Miete wurde gestartet.',
        });
        registerContainerRentNotification(startedId);

        return bookContainerRent([...selected], billedUntil);
    }, [selected, billedUntil, clear, pushToast, bookContainerRent]);

    const tableModeToolbar = useContainerStockTableModeToolbar(tableModeProps);
    const toolbarElements = useCallback(
        (elements: ToolbarOptions[]) => [
            ...tableModeToolbar(elements),
            <FlexColumn key="billedFrom" width="13rem" shrink={false} align="stretch">
                <DateInputBase
                    label="Frühestens ab"
                    name="rent_billed_from"
                    type="day"
                    ariaLabel="Frühestens ab"
                    value={billedFrom}
                    maxDate={dateOf(billedUntil)}
                    onChange={(value) => {
                        if (value === '' || value > billedUntil) {
                            return false;
                        }

                        setBilledFrom(value);
                        return true;
                    }}
                />
            </FlexColumn>,
            <FlexColumn key="billedUntil" width="13rem" shrink={false} align="stretch">
                <DateInputBase
                    label="Stichtag"
                    name="rent_billed_until"
                    type="day"
                    ariaLabel="Stichtag"
                    value={billedUntil}
                    minDate={dateOf(billedFrom)}
                    maxDate={new Date()}
                    onChange={(value) => {
                        if (value === '' || value < billedFrom || isInTheFuture(value, new Date())) {
                            return false;
                        }

                        setBilledUntil(value);
                        return true;
                    }}
                />
            </FlexColumn>,
        ],
        [billedFrom, billedUntil, tableModeToolbar],
    );
    const canBook = hasPermission(Permission.AuftragsbearbeitungContainerRentCreate);

    return (
        <>
            <TableWithStoredSettingsAndLayoutSettings<ApiUnbilledContainerRentGroup>
                layoutSettingsContext={EnumFrontendLayoutSetting.UnbilledContainerRentTableLayout}
                name="UnbilledContainerRentList"
                queryKey={['UnbilledContainerRentList', billedFrom, billedUntil]}
                columns={unbilledContainerRentColumns}
                loader={loadGroups}
                refetchRef={refetchRef}
                toolbarElements={toolbarElements}
                objectSelector={canBook ? groupSelector : undefined}
                disableExpanding={(group) => group.count <= 1}
                renderSubComponent={(group) => (
                    <ContainerStockLines
                        name="unbilled_container_rent_lines"
                        group={group}
                        columns={unbilledContainerRentLineColumns}
                        objectSelector={canBook ? lineSelectorOf(group) : undefined}
                    />
                )}
                forceExpand={{ expanded, setExpanded }}
                onSelect={(group) => {
                    if (group.count > 1) {
                        setExpanded((current) => toggledExpansion(current, group.id));
                    }
                    return true;
                }}
                canCreate={false}
                fullSize
            />
            {canBook && selected.size > 0 && (
                <FloatingToolbar>
                    <Badge variant="success" type="input">
                        {selected.size}
                    </Badge>
                    <Button variant="success" onClick={startBooking} locked={isBookingPending}>
                        <IconCircleCheck size={defaultIconSize} />
                        &nbsp;Miete abrechnen
                    </Button>
                </FloatingToolbar>
            )}
        </>
    );
}
